import { configureStore, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import config from "./config.json";
import { signOutUser } from './firebase/authHelpers';

let version = "v1"; // Example version, adjust as needed
let apiPrefix = "/api";
let baseUrl = config.api.baseUrl || "http://localhost:8000"; // Fallback to localhost if not set
let apiUrl = `${baseUrl}${apiPrefix}/${version}`;

/** API may return legacy numeric `stock` or nested `{ quantity, ... }`; admin UI expects a number on `product.stock`. */
export function normalizeProductStockForUi(product) {
  if (!product || typeof product !== "object") return product;
  const s = product.stock;
  let qty = 0;
  if (typeof s === "number" && Number.isFinite(s)) qty = Math.max(0, s);
  else if (s && typeof s === "object" && !Array.isArray(s) && s.quantity != null) {
    const q = Number(s.quantity);
    if (Number.isFinite(q)) qty = Math.max(0, q);
  }
  return { ...product, stock: qty };
}

export const fetchAddresses = createAsyncThunk(
  "addresses/fetch",
  async (userId) => {
    const res = await axios.get(`${apiUrl}/users/${userId}/address`);
    return res.data;
  }
);

export const addAddress = createAsyncThunk(
  "addresses/add",
  async ({ userId, address }) => {
    const res = await axios.post(`${apiUrl}/users/${userId}/address`, { address });
    return res.data;
  }
);

export const editAddress = createAsyncThunk(
  "addresses/edit",
  async ({ userId, address }) => {
    let addressId = address._id; // Ensure address has an id
    const res = await axios.put(`${apiUrl}/users/${userId}/address/${addressId}`, { address });
    return res.data;
  }
);

export const deleteAddress = createAsyncThunk(
  "addresses/delete",
  async ({ userId, addressId }) => {
    const res = await axios.delete(`${apiUrl}/users/${userId}/address/${addressId}`);
    return res.data;
  }
);

export const setDefaultAddress = createAsyncThunk(
  "addresses/setDefault",
  async ({ userId, addressId }) => {
    const res = await axios.put(`${apiUrl}/users/${userId}/address/${addressId}/default`);
    return res.data;
  }
);

export const performLogOut = createAsyncThunk(
  "user/logout",
  async (_, { rejectWithValue }) => {
    try {
      // Sign out from Firebase first
      await signOutUser();

      // Then notify backend to clean up any server-side session (if needed)
      const response = await axios.post(`${apiUrl}/users/logout`);
      return response.data;
    } catch (error) {
      // Still sign out locally even if backend call fails
      await signOutUser();
      return rejectWithValue(error.response?.data);
    }
  }
);

// --- USER THUNKS ---
export const fetchUserInfo = createAsyncThunk('user/fetchUserInfo', async () => {
  const response = await axios.get(`${apiUrl}/users/userInfo`);
  return response.data;
});

export const fetchUserById = createAsyncThunk(
  "user/fetchById",
  async (userId) => {
    const res = await axios.get(`${apiUrl}/users/${userId}`);
    return res.data;
  }
);

export const updateUserById = createAsyncThunk(
  "user/updateById",
  async ({ userId, data }) => {
    const res = await axios.put(`${apiUrl}/users/${userId}`, data);
    return res.data;
  }
);

// --- USERS ADMIN THUNK ---
export const fetchAllUsers = createAsyncThunk(
  "users/fetchAll",
  async () => {
    const res = await axios.get(`${apiUrl}/users`);
    return res.data;
  }
);

// --- CART THUNKS ---
export const fetchCart = createAsyncThunk(
  "cart/fetch",
  async (userId) => {
    const res = await axios.get(`${apiUrl}/users/${userId}/cart`);
    return res.data;
  }
);

export const updateCartItemQuantity = createAsyncThunk(
  "cart/updateItemQuantity",
  async ({ userId, product, quantity }) => {
    const productId = typeof product === 'object' ? product._id || product.id : product;
    const res = await axios.post(`${apiUrl}/users/${userId}/cart`, { product: { _id: productId }, quantity });
    return res.data;
  }
);


export const addOrUpdateCartItem = createAsyncThunk(
  "cart/addOrUpdateItem",
  async ({ userId, product, quantity }, { getState }) => {
    const state = getState();
    const isLoggedIn = state.auth.loggedIn;
    if (isLoggedIn) {
      const productId = typeof product === 'object' ? product._id || product.id : product;
      const res = await axios.post(`${apiUrl}/users/${userId}/cart`, { product: { _id: productId }, quantity });
      return res.data;
    } else {
      // Save to localStorage
      let localCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      // If product exists, update quantity, else add
      const idx = localCart.findIndex(item => item.product._id === product._id);
      if (idx !== -1) {
        localCart[idx].quantity = quantity;
      } else {
        localCart.push({ product, quantity });
      }
      localStorage.setItem('guest_cart', JSON.stringify(localCart));
      // Return localCart for reducer
      return localCart;
    }
  }
);

export const removeCartItem = createAsyncThunk(
  "cart/removeItem",
  async ({ userId, productId }) => {
    const res = await axios.delete(`${apiUrl}/users/${userId}/cart/item`, {
      data: { productId: productId }
    });
    return res.data;
  }
);

export const clearCart = createAsyncThunk(
  "cart/clear",
  async (userId) => {
    const res = await axios.post(`${apiUrl}/users/${userId}/cart/clear`);
    return res.data;
  }
);

// --- ORDERS THUNKS ---
export const fetchOrders = createAsyncThunk(
  "orders/fetch",
  async (userId) => {
    const res = await axios.get(`${apiUrl}/users/${userId}/orders`);
    return res.data;
  }
);

export const fetchAllOrders = createAsyncThunk(
  "orders/fetchAll",
  async () => {
    const res = await axios.get(`${apiUrl}/users/orders/all`);
    return res.data;
  }
);

export const placeOrder = createAsyncThunk(
  "orders/place",
  async ({ userId, shippingAddress }) => {
    const res = await axios.post(`${apiUrl}/users/${userId}/orders?id:${userId}`, {
      shippingAddress: shippingAddress,
      paymentMethod: "CreditCard",
    });
    return res.data;
  }
);

// Fetch order by ID thunk
export const fetchOrderById = createAsyncThunk(
  "orders/fetchById",
  async ({ userId, orderId }) => {
    const res = await axios.get(`${apiUrl}/users/${userId}/orders/${orderId}`);
    return res.data;
  }
);


export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ userId, orderId, status }) => {
    const res = await axios.patch(`${apiUrl}/users/orders/${orderId}/status`, { "orderStatus": status, orderId  });
    return res.data;
  }
);

// --- PRODUCTS THUNKS ---
export const fetchProducts = createAsyncThunk(
  "products/fetchAll",
  async (keyword = "") => {
    const res = await axios.get(`${apiUrl}/products${keyword ? `?keyword=${encodeURIComponent(keyword)}` : ""}`);
    return res.data;
  }
);

export const fetchProductById = createAsyncThunk(
  "products/fetchById",
  async (productId) => {
    const res = await axios.get(`${apiUrl}/product/${productId}`);
    return res.data;
  }
);

// --- INVENTORY MANAGEMENT THUNKS ---
export const createProduct = createAsyncThunk(
  "inventory/createProduct",
  async (productData) => {
    const res = await axios.post(`${apiUrl}/products`, productData);
    toast.success("Product created successfully!");
    return res.data;
  }
);

export const updateProduct = createAsyncThunk(
  "inventory/updateProduct",
  async ({ productId, data }) => {
    const res = await axios.put(`${apiUrl}/products/${productId}`, data);
    toast.success("Product updated successfully!");
    return res.data;
  }
);

export const deleteProduct = createAsyncThunk(
  "inventory/deleteProduct",
  async (productId) => {
    const res = await axios.delete(`${apiUrl}/products/${productId}`);
    toast.success("Product deleted successfully!");
    return res.data;
  }
);

export const updateStock = createAsyncThunk(
  "inventory/updateStock",
  async ({ productId, quantity, operation, reason, notes }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${apiUrl}/products/${productId}/stock`, {
        operation, // 'add', 'subtract', 'set'
        quantity,
        reason,
        notes
      });
      toast.success(`Stock ${operation === 'add' ? 'added' : operation === 'subtract' ? 'deducted' : 'updated'} successfully!`);
      return res.data;
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue(data ?? { message: error.message });
    }
  }
);

export const deductStockForOrder = createAsyncThunk(
  "inventory/deductStockForOrder",
  async ({ orderId }) => {
    const res = await axios.post(`${apiUrl}/orders/${orderId}/deduct-stock`);
    return res.data;
  }
);

export const restoreStockForOrder = createAsyncThunk(
  "inventory/restoreStockForOrder",
  async ({ orderId }) => {
    const res = await axios.post(`${apiUrl}/orders/${orderId}/restore-stock`);
    toast.success("Stock restored for cancelled order");
    return res.data;
  }
);

export const validateCartStock = createAsyncThunk(
  "inventory/validateCartStock",
  async ({ cartItems }) => {
    const res = await axios.post(`${apiUrl}/cart/validate-stock`, { cartItems });
    return res.data;
  }
);

export const fetchInventoryMetrics = createAsyncThunk(
  "inventory/fetchMetrics",
  async () => {
    const res = await axios.get(`${apiUrl}/inventory/metrics`);
    return res.data;
  }
);

export const fetchStockAlerts = createAsyncThunk(
  "inventory/fetchAlerts",
  async () => {
    const res = await axios.get(`${apiUrl}/inventory/alerts`);
    return res.data;
  }
);

// --- ORDERS THUNK (for placing new order) ---
export const createOrder = createAsyncThunk(
  "orders/create",
  async ({ orderItems, shippingAddress, paymentMethod }) => {
    const res = await axios.post(`${apiUrl}/orders`, { orderItems, shippingAddress, paymentMethod });
    return res.data;
  }
);

// --- USER SLICE (add extraReducers for thunks) ---
const userSlice = createSlice({
  name: "user",
  initialState: {
    userId: null,
    firstName: "",
    lastName: "",
    fullName: "",
    username: "",
    email: "",
    phone: "",
    dob: "",
    gender: "",
    profilePicture: "",
    loading: false,
    error: null,
  },
  reducers: {
    setUserDetails(state, action) {
      action.payload.userId = action.payload._id || state.userId;
      Object.assign(state, action.payload);
    },
    clearUserDetails(state) {
      Object.keys(state).forEach(key => state[key] = "");
      state.userId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        action.payload.userId = action.payload._id || state.userId;
        Object.assign(state, action.payload);
        state.loading = false;
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUserById.pending, (state) => { state.loading = true; })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        action.payload.userId = action.payload._id || state.userId;
        Object.assign(state, action.payload);
        state.loading = false;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateUserById.fulfilled, (state, action) => {
        const updatedId = action.payload._id || action.payload.userId;
        if (state._id === updatedId || state.userId === updatedId) {
          action.payload.userId = updatedId || state.userId;
          Object.assign(state, action.payload);
        }
      })
      .addCase(performLogOut.fulfilled, (state, action) => {
        state.loading = false;
        Object.assign(state, {
          userId: null,
        });
      });
  }
});

// --- CART SLICE (add extraReducers for user updates) ---
const cartSlice = createSlice({
  name: "cart",
  initialState: {
    items: JSON.parse(localStorage.getItem('guest_cart') || '[]'),
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => { state.loading = true; })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.items = [...action.payload];
        state.loading = false;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to load cart.");
      })
      .addCase(updateCartItemQuantity.fulfilled, (state, action) => {
        state.items = [...action.payload];
      })
      .addCase(updateCartItemQuantity.rejected, (state, action) => {
        toast.error("Failed to add/update product in cart.");
      })
      .addCase(addOrUpdateCartItem.fulfilled, (state, action) => {
        toast.success("Product added/updated in cart successfully.");
        state.items = [...action.payload];
        // If not logged in, also update localStorage
        const isLoggedIn = state?.auth?.loggedIn;
        if (!isLoggedIn) {
          localStorage.setItem('guest_cart', JSON.stringify(state.items));
        }
      })
      .addCase(addOrUpdateCartItem.rejected, (state, action) => {
        toast.error("Failed to add/update product in cart.");
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        state.items = [...action.payload];
      })
      .addCase(removeCartItem.rejected, (state, action) => {
        toast.error("Failed to remove product from cart.");
      })
      .addCase(clearCart.fulfilled, (state, action) => {
        state.items = [];
      })
      .addCase(clearCart.rejected, (state, action) => {
        toast.error("Failed to clear cart.");
      })
      // Update cart state whenever user state is updated and cart is present in payload
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        // On login, move guest cart to user cart if present
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        if (guestCart.length > 0 && action.payload.cart) {
          // Merge guest cart into user cart (simple append, can be improved)
          const merged = [...action.payload.cart];
          guestCart.forEach(guestItem => {
            const idx = merged.findIndex(item => item.product._id === guestItem.product._id);
            if (idx !== -1) {
              merged[idx].quantity += guestItem.quantity;
            } else {
              merged.push(guestItem);
            }
          });
          state.items = merged;
          localStorage.removeItem('guest_cart');
        } else if (action.payload.cart) {
          state.items = [...action.payload.cart];
        }
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        if (action.payload.cart) {
          state.items = [...action.payload.cart];
        }
      })

      .addCase(performLogOut.fulfilled, (state, action) => {
        state.loading = false;
        state.items = []; // Clear cart on logout
        toast.info("Logged out successfully.");
      });
  }
});

// --- ORDERS SLICE (add extraReducers for thunks) ---
const ordersSlice = createSlice({
  name: "orders",
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {
    setOrders(state, action) {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    addOrder(state, action) {
      state.items.push(action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.items = action.payload;
        state.fetched = true; // Mark orders as fetched
        state.loading = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to load orders.");
      })
      .addCase(fetchAllOrders.fulfilled, (state, action) => {
        state.items = action.payload;
        state.fetched = true; // Mark orders as fetched
        state.loading = false;
      })
      .addCase(fetchAllOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to load orders.");
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.items.push(action.payload);
        toast.success("Order placed successfully!");
      })
      .addCase(placeOrder.rejected, (state, action) => {
        toast.error("Failed to place order.");
      })
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.items.push(action.payload.order || action.payload); // adapt to your API response
        state.loading = false;
        toast.success("Order created successfully!");
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to create order.");
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        let item = state.items.find(order => order._id === action.payload.order._id);
        item.orderStatus = action.payload.order.orderStatus;
        toast.success("Order status updated successfully!");
      })
      .addCase(performLogOut.fulfilled, (state, action) => {
        state.loading = false;
        state.items = []; // Clear orders on logout
      });;
  }
});

// Products slice
const productsSlice = createSlice({
  name: "products",
  initialState: {
    items: [],
    product: null,
    loading: false,
    error: null,
  },
  reducers: {
    setProducts(state, action) {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.fetched = true;
        const raw = action.payload.products || action.payload;
        state.items = Array.isArray(raw) ? raw.map(normalizeProductStockForUi) : [];
        state.loading = false;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        const p = action.payload.product || action.payload;
        state.product = p ? normalizeProductStockForUi(p) : null;
        state.loading = false;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

// --- ADDRESSES SLICE ---
const addressesSlice = createSlice({
  name: "addresses",
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to load addresses.");
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(addAddress.rejected, (state, action) => {
        toast.error("Failed to add address.");
      })
      .addCase(editAddress.fulfilled, (state, action) => {
        const idx = state.items.findIndex(addr => addr.id === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        }
        toast.success("Address updated!");
      })
      .addCase(editAddress.rejected, (state, action) => {
        toast.error("Failed to update address.");
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.items = state.items.filter(addr => addr.id !== action.payload.id);
        toast.success("Address deleted!");
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        toast.error("Failed to delete address.");
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.items = state.items.map(addr => ({
          ...addr,
          isDefault: addr.id === action.payload.id
        }));
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        toast.error("Failed to set default address.");
      })
      .addCase(performLogOut.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = []; // Clear addresses on logout
      }
      );
  }
});

// --- ADDRESSES SLICE ---
const usersSlice = createSlice({
  name: "users",
  initialState: {
    items: [],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to load addresses.");
      })
      .addCase(performLogOut.fulfilled, (state, action) => {
        state.loading = false;
        state.items = []; 
      })
      .addCase(updateUserById.fulfilled, (state, action) => {
        const updatedId = action.payload._id || action.payload.userId;
        const index = state.items.findIndex(user => user._id === updatedId || user.userId === updatedId);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
      });
  }
});

// Auth slice
const authSlice = createSlice({
  name: "auth",
  initialState: {
    loggedIn: false, // default to false
    authInitialized: false,
  },
  reducers: {
    setAuthInitialized(state, action) {
      state.authInitialized = action.payload;
    },
    setLoggedIn(state, action) {
      state.loggedIn = action.payload;
    },
    logout(state) {
      state.loggedIn = false;
    },
    login(state) {
      state.loggedIn = true;
    },
  },
  extraReducers: (builder) => {
    const setLoggedOutOn401 = (state, action) => {
      if (
        (action.error && action.error.message && action.error.message.includes("401")) ||
        (action.error && action.error.code === "ERR_BAD_REQUEST" && action.error.message.includes("401")) ||
        (action.meta && action.meta.rejectedWithValue && action.payload && action.payload.status === 401)
      ) {
        state.loggedIn = false;
        // toast.error("Session expired. Please log in again.");
      }
    };

    builder
      .addCase(fetchUserInfo.fulfilled, (state) => {
        state.loggedIn = true;
      })
      .addCase(fetchUserInfo.rejected, setLoggedOutOn401)
      .addCase(fetchUserById.rejected, setLoggedOutOn401)
      .addCase(updateUserById.rejected, setLoggedOutOn401)
      .addCase(fetchCart.rejected, setLoggedOutOn401)
      .addCase(addOrUpdateCartItem.rejected, setLoggedOutOn401)
      .addCase(updateCartItemQuantity.rejected, setLoggedOutOn401)
      .addCase(removeCartItem.rejected, setLoggedOutOn401)
      .addCase(clearCart.rejected, setLoggedOutOn401)
      .addCase(fetchOrders.rejected, setLoggedOutOn401)
      .addCase(placeOrder.rejected, setLoggedOutOn401)
      .addCase(createOrder.rejected, setLoggedOutOn401)
      .addCase(fetchAddresses.rejected, setLoggedOutOn401)
      .addCase(addAddress.rejected, setLoggedOutOn401)
      .addCase(editAddress.rejected, setLoggedOutOn401)
      .addCase(deleteAddress.rejected, setLoggedOutOn401)
      .addCase(setDefaultAddress.rejected, setLoggedOutOn401)
      .addCase(performLogOut.fulfilled, (state) => {
        state.loggedIn = false;
      });
  }
});

export const {
  setProducts,
  setLoading,
  setError,
} = productsSlice.actions;

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart: clearCartAction,
} = cartSlice.actions;

export const {
  setUserDetails,
  clearUserDetails,
} = userSlice.actions;

export const {
  setOrders,
  addOrder,
  setOrdersLoading,
  setOrdersError,
  clearOrders,
} = ordersSlice.actions;

export const { setLoggedIn, logout, login, setAuthInitialized } = authSlice.actions;

const store = configureStore({
  reducer: {
    products: productsSlice.reducer,
    cart: cartSlice.reducer,
    user: userSlice.reducer,
    orders: ordersSlice.reducer,
    addresses: addressesSlice.reducer,
    auth: authSlice.reducer,
    users: usersSlice.reducer,
  },
});

export default store;